import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { IAcceptanceData } from 'src/app/models/acceptance.data.model';
import { grupoBenesView } from 'src/app/models/pacote-beneficio.models';
import { PropostaSalarialView } from 'src/app/models/proposta-salarial.model';
import { BeneficioService } from 'src/app/services/pacote-beneficio.service';
import { PropostaSalarialService } from 'src/app/services/proposta-salarial.service';
import { getCurrency } from 'src/app/utils/helpers/currency.helper';
import { AceiteDigitalService } from 'src/app/services/aceite-digital.service';
import { IRejectionData } from 'src/app/models/rejection.data.model';
import { CO_STATUS_CONST } from 'src/app/utils/utils';
import { IOlNamesConfig } from 'src/app/models/custom-names.model';
import { OlNamesService } from 'src/app/services/ol-names.service';
import { IAcceptanceDataWithoutFlagData } from 'src/app/models/acceptance.data.without-flag.model';
import { formatDayMonthLong } from 'src/app/utils/day-month.utils';

@Component({
  selector: 'hr-ol-pdf-acceptance',
  templateUrl: './ol-pdf-acceptance.component.html',
  styleUrls: ['./ol-pdf-acceptance.component.scss'],
  providers: [BeneficioService, OlNamesService, OlNamesService],
  encapsulation: ViewEncapsulation.None,
})
export class OlPdfAcceptanceComponent implements OnInit, OnDestroy {
  @ViewChild('pdfArea', { static: true }) pdfArea!: ElementRef<HTMLDivElement>;
  public propostaSalarial: PropostaSalarialView;
  public acceptanceData: Partial<IAcceptanceData>;
  public rejectionData: Partial<IRejectionData>;
  public acceptanceDataWithoutFlag: Partial<IAcceptanceDataWithoutFlagData>;
  public coStatusConstants = CO_STATUS_CONST;
  public gruposBenes: grupoBenesView[];
  public hasCustomOrder = false;
  public benesList: { name: string; description: string }[] = [];
  public currentTemplate;
  public acceptanceCardAppeareance = {
    fontSize: '25px',
    padding: '2.5rem',
    maxWidth: '90%',
    alignItems: 'center',
    iconWidth: '2rem',
    iconHeight: '2rem',
    svgWidth: '20px',
  };
  public getCurrencyHelper = getCurrency;
  private destroy$ = new Subject<void>();
  variableWage_1: any;
  variableWage_2: any;
  grupoSalarialLabel: string;
  constructor(
    private propostaService: PropostaSalarialService,
    private beneficioService: BeneficioService,
    private aceiteDigitalService: AceiteDigitalService,
    private olNamesService: OlNamesService
  ) {
    this.propostaSalarial = this.propostaService.getView();
  }

  async ngOnInit(): Promise<void> {
    await this.getGruposBenes();
    this.observeAcceptanceData();
    this.observeRejectionData();
    this.observeAcceptanceDataWithoutFlag();
    const olNames = await this.olNamesService.getOlNames();
    this.variableWage_1 = olNames.variable_wage_1;
    this.variableWage_2 = olNames.variable_wage_2;
    this.grupoSalarialLabel = olNames.grupo_salarial;
    this.refreshTemplate();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getGruposBenes() {
    const grupos = await this.beneficioService.getGrupos();
    this.gruposBenes = grupos;
    this.hasCustomOrder = this.beneficioService.hasCustomOrder();
    this.benesList = this.hasCustomOrder ? this.beneficioService.getFlatSortedBenefits() : [];
  }

  private observeAcceptanceData(): void {
    this.propostaService
      .getAcceptanceData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((acceptanceData) => {
        this.acceptanceData = acceptanceData;
        const template = this.propostaService.getTemplate();
        if (!template?.slides) {
          return;
        }
        this.currentTemplate = this.mapTemplateSlides(template);
      });
  }

  private observeRejectionData(): void {
    this.propostaService
      .getRejectionData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((rejectionData) => {
        this.rejectionData = rejectionData;
        const template = this.propostaService.getTemplate();
        if (!template?.slides) {
          return;
        }
        this.currentTemplate = this.mapTemplateSlides(template);
      });
  }

  private observeAcceptanceDataWithoutFlag(): void {
    this.propostaService
      .getAcceptanceDataWithoutFlag()
      .pipe(takeUntil(this.destroy$))
      .subscribe((acceptanceDataWithoutFlag) => {
        this.acceptanceDataWithoutFlag = acceptanceDataWithoutFlag;
        const template = this.propostaService.getTemplate();
        if (!template?.slides) {
          return;
        }
        this.currentTemplate = this.mapTemplateSlides(template);
      });
  }

  private mapTemplateSlides(template: any) {
    return template.slides.reduce((acc: any, slide: any) => {
      if (slide.type === 'cover') {
        acc.cover = {
          title: slide.title,
          description: slide.description,
        };
      }
      if (slide.type === 'welcome') {
        acc.welcome = {
          fullName: this.propostaSalarial.nomeCompleto,
          description: slide.description,
        };
      }
      if (slide.type === 'offer-detail') {
        acc.offerDetails = {
          wage: this.propostaSalarial.salario,
          salaryType: this.propostaSalarial.salaryType,
          wageSubtitle: slide.wageSubtitle,
          showVariableWage: slide.showVariableWage,
          variableWage: slide.variableWage,
          variableWageSubtitle: slide.variableWageSubtitle,
          note: slide.note,
          managerName: this.propostaSalarial.gestorName,
          workSite: this.propostaSalarial.localTrabalho,
          workModel: this.propostaSalarial.modeloTrabalho,
          admissionDate: this.propostaSalarial.dataAdmissao
            ? this.propostaSalarial.dataAdmissao.toDate().toLocaleDateString()
            : null,
          workload: this.propostaSalarial.cargaHoraria,
          workSchedule: this.propostaSalarial.escalaTrabalho,
          workHours: this.propostaSalarial.jornadaTrabalho,
          baseDate: this.propostaSalarial.dataBase ? formatDayMonthLong(this.propostaSalarial.dataBase) : null,
          secondaryCall: slide.secondaryCall,
          description: slide.description,
          variableWage_1: this.variableWage_1,
          variableWage_2: this.variableWage_2,
          hasILP: this.propostaSalarial.hasILP,
          hasHiringBonus: this.propostaSalarial.hasHiringBonus,
          hiringBonus: this.propostaSalarial.hiringBonus,
          iLP: this.propostaSalarial.iLP,
          position: this.propostaSalarial.cargo,
          company: this.propostaSalarial.companyName,
          grupoSalarialName: this.propostaSalarial.grupoSalarialName,
          grupoSalarialLabel: this.grupoSalarialLabel,
          remuneracaoExtra: (this.beneficioService.remuneracaoBenefits ?? []).map((bene) => ({
            title: bene.name,
            description: bene.description,
          })),
        };
      }
      if (slide.type === 'benefits') {
        acc.benefits = {
          description: slide.description,
          title: slide.title,
          gruposBenes: this.gruposBenes,
          hasCustomOrder: this.hasCustomOrder,
          benesList: this.benesList,
        };
      }
      acc.acceptanceData = this.acceptanceData;
      acc.rejectionData = this.rejectionData;
      acc.acceptanceDataWithoutFlag = this.acceptanceDataWithoutFlag;
      return acc;
    }, {});
  }

  public refreshTemplate(): void {
    const template = this.propostaService.getTemplate();
    if (template?.slides) {
      this.currentTemplate = this.mapTemplateSlides(template);
    }
  }

  get shouldShowDigitalAcceptanceCard(): boolean {
    return this.aceiteDigitalService.currentDigitalAcceptance;
  }
}
